import { useMemo } from "react";

import { generateLeadsSearchUrl } from "components/leads/utils";
import toast from "components/ui/toast/Toast";
import { useCreateLeadWorkflow } from "lib/hooks/api/leads/useCreateLeadWorkflow";
import { useLeadWorkflow } from "lib/hooks/api/leads/useLeadWorkflow";
import { useUpdateLeadWorkflow } from "lib/hooks/api/leads/useUpdateLeadWorkflow";
import { Content, Page } from "./shared";
import { CustomiseWorkflowInput, mapLeadWorkflowToWorkflow, toCreateTransition } from "./utils";
import WorkflowContainer from "./WorkflowContainer";

export default function LeadWorkflow(): JSX.Element {
  const { data, isLoading, isError } = useLeadWorkflow({ withCounts: true });

  const { mutate: updateLeadWorkflow } = useUpdateLeadWorkflow({
    onSuccess: () => {
      toast.success("Lead workflow updated successfully");
    },
    onError: () => toast.error("Failed to update lead workflow"),
  });

  const { mutate: createLeadWorkflow } = useCreateLeadWorkflow({
    onSuccess: () => {
      toast.success("Lead workflow updated successfully");
    },
    onError: () => toast.error("Failed to update lead workflow"),
  });

  const workflow = useMemo(() => (data ? mapLeadWorkflowToWorkflow(data) : undefined), [data]);

  const handleWorkflowChange = (input: CustomiseWorkflowInput) => {
    const { transitions, stageReplacements } = input;

    if (input.isDefault) {
      createLeadWorkflow({
        createWorkflow: {
          transitions: transitions.map(toCreateTransition),
          stageReplacements,
        },
        stageFilterRequest: {},
      });
    } else {
      updateLeadWorkflow({
        updateWorkflow: {
          id: input.workflowId,
          transitions,
          // No replacements are needed because IDs remain unchanged when updating workflows
          stageReplacements: [],
        },
        stageFilterRequest: {},
      });
    }
  };

  return (
    <Page>
      <Content>
        <WorkflowContainer
          workflow={workflow}
          onWorkflowChange={handleWorkflowChange}
          isError={isError}
          isLoading={isLoading}
          title="Customise your Lead workflow"
          workflowType="lead"
          generateSearchUrl={(stageId) => generateLeadsSearchUrl({ stageIds: [stageId] })}
        />
      </Content>
    </Page>
  );
}
